import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  X, 
  Upload, 
  Link, 
  FileText, 
  Plus, 
  Trash2,
  Building2,
  Calendar,
  Tag,
  Star,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';
import { theme } from '../styles/theme';
import { TenderDocumentApi, TenderDocumentCreateRequest } from '../services/tenderDocumentApi';
import { useToastNotifications } from '../context/ToastContext';
import { uploadService } from '../services/uploadService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing[6]}; /* Increased padding to prevent edge clipping */
  box-sizing: border-box;
`;

const ModalContainer = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${theme.shadows.xl};
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0; /* Prevent header from shrinking */
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  border-radius: ${theme.borderRadius.lg};
  color: white;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    background: ${theme.colors.error[100]};
    border-color: ${theme.colors.error[500]};
    color: ${theme.colors.error[600]};
  }
`;

const ModalContent = styled.div`
  padding: ${theme.spacing[6]};
  flex: 1;
  overflow-y: auto;
  min-height: 0; /* Important for flex child with overflow */
`;

const TabNavigation = styled.div`
  display: flex;
  gap: ${theme.spacing[1]};
  margin-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: none;
  color: ${({ $active }) => $active ? theme.colors.primary[600] : theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border-bottom: 2px solid ${({ $active }) => $active ? theme.colors.primary[500] : 'transparent'};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  
  &:hover {
    color: ${theme.colors.primary[600]};
  }
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const DropZone = styled.div<{ $isDragOver?: boolean }>`
  border: 2px dashed ${({ $isDragOver }) => $isDragOver ? theme.colors.primary[500] : theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[8]};
  text-align: center;
  background: ${({ $isDragOver }) => $isDragOver ? theme.colors.primary[50] : theme.colors.backgroundSecondary};
  transition: all ${theme.transitions.base};
  cursor: pointer;
  
  &:hover {
    border-color: ${theme.colors.primary[400]};
    background: ${theme.colors.primary[50]};
  }
`;

const DropZoneIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${theme.spacing[4]} auto;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary[600]};
`;

const DropZoneTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const DropZoneDescription = styled.p`
  color: ${theme.colors.textMuted};
  margin: 0;
`;

const UrlInputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const UrlInputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: flex-end;
`;

const InputGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const InputLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const TextInput = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.base};
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: ${theme.shadows.focus};
  }
  
  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const AddUrlButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    background: ${theme.colors.primary[600]};
  }
  
  &:disabled {
    background: ${theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

const UrlList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  max-height: 200px;
  overflow-y: auto;
`;

const UrlItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
`;

const UrlText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  flex: 1;
  margin-right: ${theme.spacing[3]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveUrlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    background: ${theme.colors.error[100]};
    border-color: ${theme.colors.error[500]};
    color: ${theme.colors.error[600]};
  }
`;

const FormSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[6]};
`;

const SelectInput = styled.select`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.surface};
  transition: all ${theme.transitions.base};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: ${theme.shadows.focus};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};
  gap: ${theme.spacing[4]};
  flex-shrink: 0; /* Prevent footer from shrinking */
  background: ${theme.colors.surface}; /* Ensure background color */
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
`;

const FooterButtons = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border: 1px solid;
  
  ${({ $variant = 'secondary' }) => {
    if ($variant === 'primary') {
      return `
        background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
        color: white;
        border-color: transparent;
        
        &:hover {
          transform: translateY(-1px);
          box-shadow: ${theme.shadows.lg};
        }
        
        &:disabled {
          background: ${theme.colors.textMuted};
          transform: none;
          box-shadow: none;
          cursor: not-allowed;
        }
      `;
    } else {
      return `
        background: ${theme.colors.surface};
        color: ${theme.colors.textSecondary};
        border-color: ${theme.colors.border};
        
        &:hover {
          background: ${theme.colors.backgroundSecondary};
          border-color: ${theme.colors.primary[300]};
          color: ${theme.colors.textPrimary};
        }
      `;
    }
  }}
`;

const StatusIndicator = styled.div<{ $status: 'idle' | 'uploading' | 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  
  ${({ $status }) => {
    switch ($status) {
      case 'uploading':
        return `
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
        `;
      case 'success':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `;
      case 'error':
        return `
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
        `;
      default:
        return 'display: none;';
    }
  }}
`;

interface TenderUpload {
  urls?: string[];
  files?: File[];
  metadata?: {
    description: string;
    resourceType: string;
    industry: string;
    score?: number;
  };
}

interface TenderUploadModalProps {
  onClose: () => void;
  onUpload: (documents: TenderUpload) => void;
}

const TenderUploadModal: React.FC<TenderUploadModalProps> = ({ onClose, onUpload }) => {
  const [activeTab, setActiveTab] = useState<'url' | 'file'>('file');
  const [currentUrl, setCurrentUrl] = useState('');
  const [urls, setUrls] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [industry, setIndustry] = useState('');
  const [score, setScore] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [isDragOver, setIsDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { showSuccess, showError } = useToastNotifications();

  const handleAddUrl = () => {
    if (currentUrl.trim() && !urls.includes(currentUrl.trim())) {
      setUrls([...urls, currentUrl.trim()]);
      setCurrentUrl('');
    }
  };

  const handleRemoveUrl = (urlToRemove: string) => {
    setUrls(urls.filter(url => url !== urlToRemove));
  };

  const handleFileUpload = (uploadedFiles: FileList | File[]) => {
    const fileArray = Array.from(uploadedFiles);
    const validFiles = fileArray.filter(file =>
      file.type === 'application/pdf' ||
      file.type.startsWith('text/') ||
      file.name.endsWith('.doc') ||
      file.name.endsWith('.docx')
    );
    setFiles([...files, ...validFiles]);

    // Auto-fill title from the first valid filename when title is blank.
    // Most uploads are "drop a file → save"; the title field is signal,
    // not noise, so derive it instead of asking again. The user can
    // still edit before submit.
    if (!title.trim() && validFiles.length > 0) {
      const stem = validFiles[0].name
        .replace(/\.[^/.]+$/, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const prettified = stem
        .split(' ')
        .map(w => (w.length > 3 && w === w.toLowerCase()) ? w[0].toUpperCase() + w.slice(1) : w)
        .join(' ');
      setTitle(prettified);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  // Upload file using unified service
  const uploadFile = async (file: File): Promise<string> => {
    try {
      const url = await uploadService.uploadFile(file, {
        timeout: 30000
      });
      
      return url;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload file');
    }
  };

  const handleUpload = async () => {
    setUploadStatus('uploading');
    setErrorMessage('');
    
    try {
      // Upload files first if any files are selected (uses Cloudinary locally, S3 in production)
      const uploadedFileUrls: string[] = [];
      if (files.length > 0) {
        for (const file of files) {
          const fileUrl = await uploadFile(file);
          uploadedFileUrls.push(fileUrl);
        }
      }

      // Combine manual URLs with uploaded file URLs
      const allUrls = [...urls, ...uploadedFileUrls];

      // Backend still requires a non-empty description (min_length=1). When
      // the user leaves it blank we derive one from the filename / title /
      // resource type so the request stays valid without nagging the user
      // for redundant text.
      const derivedDescription = (() => {
        const trimmed = description.trim();
        if (trimmed) return trimmed;
        const fileBasis = files[0]?.name?.replace(/\.[^/.]+$/, '') || '';
        const titleBasis = title.trim();
        const labelBasis = titleBasis || fileBasis || 'Untitled tender';
        return `${resourceType || 'Tender'} document — ${labelBasis}`.slice(0, 2000);
      })();

      // Prepare the API request
      const request: TenderDocumentCreateRequest = {
        title: title.trim() || undefined,
        description: derivedDescription,
        resource_type: resourceType,
        industry,
        score: score ? parseFloat(score) : undefined,
        urls: allUrls.length > 0 ? allUrls : undefined
      };
      
      // Call the API
      const response = await TenderDocumentApi.createTenderDocument(request);
      
      if (response.success && response.data) {
        setUploadStatus('success');
        
        // Show success notification
        showSuccess(
          'Document Uploaded Successfully',
          `"${response.data.title}" has been added to your tender bank.`
        );
        
        // Call the original onUpload callback with the created document data
        const uploadData: TenderUpload = {
          urls: urls.length > 0 ? urls : undefined,
          files: files.length > 0 ? files : undefined,
          metadata: {
            description,
            resourceType,
            industry,
            score: response.data.score
          }
        };
        
        setTimeout(() => {
          onUpload(uploadData);
        }, 1000);
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      setUploadStatus('error');
      const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred during upload';
      setErrorMessage(errorMsg);
      
      // Show error notification
      showError(
        'Upload Failed',
        errorMsg
      );
    }
  };

  // Description used to be required, but we now auto-derive a fallback at
  // submit if it's blank — most users just want to drop a file and pick
  // resource type + industry. Only those three signals matter for retrieval.
  const canUpload = (urls.length > 0 || files.length > 0) && resourceType.trim() && industry.trim();

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'uploading':
        return <Loader className="animate-spin" size={16} />;
      case 'success':
        return <CheckCircle size={16} />;
      case 'error':
        return <AlertCircle size={16} />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (uploadStatus) {
      case 'uploading':
        return 'Processing documents...';
      case 'success':
        return 'Documents uploaded successfully!';
      case 'error':
        return errorMessage || 'Upload failed. Please try again.';
      default:
        return '';
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <TitleIcon>
              <Upload size={20} />
            </TitleIcon>
            Add to Tender Bank
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <div style={{ marginBottom: theme.spacing[6] }}>
            <h3 style={{ 
              fontSize: theme.typography.fontSize.lg, 
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.textPrimary,
              marginBottom: theme.spacing[2]
            }}>
              Add Documents to Tender Bank
            </h3>
            <p style={{ 
              color: theme.colors.textSecondary, 
              fontSize: theme.typography.fontSize.sm,
              lineHeight: theme.typography.lineHeight.relaxed,
              margin: 0
            }}>
              Upload successful tender documents, RFPs, specifications, and related materials to build your knowledge base for AI-powered analysis.
            </p>
          </div>

          <TabNavigation>
            <TabButton
              $active={activeTab === 'file'}
              onClick={() => setActiveTab('file')}
            >
              <FileText size={16} />
              Upload Files
            </TabButton>
            <TabButton
              $active={activeTab === 'url'}
              onClick={() => setActiveTab('url')}
            >
              <Link size={16} />
              From URL
            </TabButton>
          </TabNavigation>

          {activeTab === 'url' && (
            <UploadSection>
              <div style={{ 
                padding: theme.spacing[4], 
                backgroundColor: theme.colors.primary[100], 
                borderRadius: theme.borderRadius.lg, 
                border: `1px solid ${theme.colors.primary[500]}`,
                marginBottom: theme.spacing[4]
              }}>
                <p style={{ 
                  color: theme.colors.primary[700], 
                  fontSize: theme.typography.fontSize.sm,
                  margin: 0,
                  fontWeight: theme.typography.fontWeight.medium
                }}>
                  🔗 Add documents from publicly accessible URLs. Perfect for government tender portals and document repositories.
                </p>
              </div>
              <UrlInputSection>
                <UrlInputContainer>
                  <InputGroup>
                    <InputLabel>Tender Document URL</InputLabel>
                    <TextInput
                      type="url"
                      value={currentUrl}
                      onChange={(e) => setCurrentUrl(e.target.value)}
                      placeholder="https://tender.com/tender-document.pdf"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
                    />
                  </InputGroup>
                  <AddUrlButton 
                    onClick={handleAddUrl}
                    disabled={!currentUrl.trim()}
                  >
                    <Plus size={16} />
                    Add
                  </AddUrlButton>
                </UrlInputContainer>

                {urls.length > 0 && (
                  <UrlList>
                    {urls.map((url, index) => (
                      <UrlItem key={index}>
                        <UrlText>{url}</UrlText>
                        <RemoveUrlButton onClick={() => handleRemoveUrl(url)}>
                          <Trash2 size={16} />
                        </RemoveUrlButton>
                      </UrlItem>
                    ))}
                  </UrlList>
                )}
              </UrlInputSection>
            </UploadSection>
          )}

          {activeTab === 'file' && (
            <UploadSection>
              <div style={{ 
                padding: theme.spacing[4], 
                backgroundColor: theme.colors.primary[100], 
                borderRadius: theme.borderRadius.lg, 
                border: `1px solid ${theme.colors.primary[500]}`,
                marginBottom: theme.spacing[4]
              }}>
                <p style={{ 
                  color: theme.colors.primary[700], 
                  fontSize: theme.typography.fontSize.sm,
                  margin: 0,
                  fontWeight: theme.typography.fontWeight.medium
                }}>
                  📋 Upload documents directly from your computer. Supports PDF, DOC, DOCX, and TXT files up to 50MB each.
                </p>
              </div>
              <DropZone
                $isDragOver={isDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = true;
                  input.accept = '.pdf,.doc,.docx,.txt';
                  input.onchange = (e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.files) {
                      handleFileUpload(target.files);
                    }
                  };
                  input.click();
                }}
              >
                <DropZoneIcon>
                  <Upload size={32} />
                </DropZoneIcon>
                <DropZoneTitle>Drop files here or click to browse</DropZoneTitle>
                <DropZoneDescription>
                  Supports PDF, DOC, DOCX, and TXT files (max 50MB each)
                </DropZoneDescription>
              </DropZone>

              {files.length > 0 && (
                <UrlList>
                  {files.map((file, index) => (
                    <UrlItem key={index}>
                      <UrlText>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</UrlText>
                      <RemoveUrlButton onClick={() => {
                        setFiles(files.filter((_, i) => i !== index));
                      }}>
                        <Trash2 size={16} />
                      </RemoveUrlButton>
                    </UrlItem>
                  ))}
                </UrlList>
              )}
            </UploadSection>
          )}

          <FormSection>
            <InputGroup style={{ gridColumn: '1 / -1' }}>
              <InputLabel>
                Title
                {files.length > 0 && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 400,
                    color: theme.colors.textMuted,
                    marginLeft: 8,
                  }}>
                    (auto-filled from filename — edit if needed)
                  </span>
                )}
              </InputLabel>
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Document title (optional)"
              />
            </InputGroup>

            <InputGroup style={{ gridColumn: '1 / -1' }}>
              <InputLabel>
                Description
                <span style={{
                  fontSize: '11px',
                  fontWeight: 400,
                  color: theme.colors.textMuted,
                  marginLeft: 8,
                }}>
                  (optional)
                </span>
              </InputLabel>
              <TextInput
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the tender, project, or requirements (we'll derive one from the filename if you skip this)"
                style={{ resize: 'vertical', minHeight: '80px' }}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Resource Type *</InputLabel>
              <SelectInput
                value={resourceType}
                onChange={(e) => setResourceType(e.target.value)}
              >
                <option value="">Select resource type</option>
                <option value="Tender">Tender Document</option>
                <option value="RFP">Request for Proposal (RFP)</option>
                <option value="RFQ">Request for Quotation (RFQ)</option>
                <option value="ITT">Invitation to Tender (ITT)</option>
                <option value="EOI">Expression of Interest (EOI)</option>
                <option value="Pre-qualification">Pre-qualification Document</option>
                <option value="Technical-Specification">Technical Specification</option>
                <option value="Contract">Contract Document</option>
                <option value="Amendment">Amendment/Addendum</option>
                <option value="Clarification">Clarification Document</option>
                <option value="Other">Other</option>
              </SelectInput>
            </InputGroup>

            <InputGroup>
              <InputLabel>Industry Sector *</InputLabel>
              <SelectInput
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              >
                <option value="">Select Industry</option>
                <option value="agriculture_food">Agriculture & Food</option>
                <option value="automotive">Automotive</option>
                <option value="aviation_aerospace">Aviation & Aerospace</option>
                <option value="banking_finance">Banking & Finance</option>
                <option value="biotechnology">Biotechnology</option>
                <option value="construction_infrastructure">Construction & Infrastructure</option>
                <option value="consulting_services">Consulting Services</option>
                <option value="cybersecurity">Cybersecurity</option>
                <option value="defense_military">Defense & Military</option>
                <option value="ecommerce">E-commerce</option>
                <option value="education_training">Education & Training</option>
                <option value="energy_utilities">Energy & Utilities</option>
                <option value="entertainment_media">Entertainment & Media</option>
                <option value="environmental_services">Environmental Services</option>
                <option value="fashion_retail">Fashion & Retail</option>
                <option value="government_public">Government & Public Services</option>
                <option value="healthcare_medical">Healthcare & Medical</option>
                <option value="insurance">Insurance</option>
                <option value="legal_services">Legal Services</option>
                <option value="logistics_transportation">Logistics & Transportation</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="mining_resources">Mining & Resources</option>
                <option value="nonprofit_ngo">Non-profit & NGO</option>
                <option value="pharmaceuticals">Pharmaceuticals</option>
                <option value="real_estate">Real Estate</option>
                <option value="research_development">Research & Development</option>
                <option value="technology_software">Technology & Software</option>
                <option value="telecommunications">Telecommunications</option>
                <option value="tourism_hospitality">Tourism & Hospitality</option>
                <option value="other">Other</option>
              </SelectInput>
            </InputGroup>

            {/* Advanced metadata — Success Score and (eventually) outcome,
                buyer, value band. Default-collapsed: most uploads come
                without an evaluator's score, so asking up-front adds
                friction. When the user does have it, the score boosts
                this tender in outcome-aware retrieval. */}
            <InputGroup style={{ gridColumn: '1 / -1' }}>
              <button
                type="button"
                onClick={() => setShowAdvanced(prev => !prev)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.colors.primary[600],
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  padding: '6px 0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {showAdvanced ? '▾' : '▸'} {showAdvanced ? 'Hide' : 'Show'} advanced metadata (success score)
              </button>
            </InputGroup>

            {showAdvanced && (
              <InputGroup>
                <InputLabel>Success Score (Optional)</InputLabel>
                <TextInput
                  type="number"
                  min="0"
                  max="100"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  placeholder="85"
                />
                <div style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.colors.textMuted,
                  marginTop: theme.spacing[1],
                }}>
                  Score achieved for this tender (0-100). Higher-scoring tenders are weighted more in retrieval.
                </div>
              </InputGroup>
            )}
          </FormSection>
        </ModalContent>

        <ModalFooter>
          <FooterLeft>
            <StatusIndicator $status={uploadStatus}>
              {getStatusIcon()}
              {getStatusText()}
            </StatusIndicator>
          </FooterLeft>
          
          <FooterButtons>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button
              $variant="primary"
              onClick={handleUpload}
              disabled={!canUpload || uploadStatus === 'uploading'}
            >
              {uploadStatus === 'uploading' ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  Processing...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Add to Tender Bank
                </>
              )}
            </Button>
          </FooterButtons>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TenderUploadModal;